import {
  additionalMetadataKey,
  createdAtKey,
  flattenDAGsKey,
  idempotencyKeyKey,
  runningFilterKey,
  statusKey,
  workflowKey,
} from '../components/v1/task-runs-columns';
import { buildMetadataFilterOptions } from './metadata-filter-options';
import { timeWindowSchema } from './time-windows';
import { FilterActions } from './use-runs-table-filters';
import { useWorkflowRunCounts } from './use-workflow-run-counts';
import { useWorkflows } from './use-workflows';
import { buildWorkflowFilterOptions } from './workflow-filter-options';
import {
  FilterOption,
  ToolbarFilters,
  ToolbarType,
  TimeRangeConfig,
} from '@/components/v1/molecules/data-table/data-table-toolbar';
import {
  V1AdditionalMetadataOperator,
  V1RunningFilter,
  V1TaskSummary,
  V1TaskStatus,
} from '@/lib/api';
import { useMemo, useState } from 'react';

const WORKFLOW_COUNT_QUERY_LIMIT = 50;

export const workflowRunStatusFilters: FilterOption[] = [
  {
    value: V1TaskStatus.COMPLETED,
    label: 'Succeeded',
  },
  {
    value: V1TaskStatus.FAILED,
    label: 'Failed',
  },
  {
    value: V1TaskStatus.RUNNING,
    label: 'Running',
    subFilterColumnId: runningFilterKey,
    subOptions: [
      { value: V1RunningFilter.ON_WORKER, label: 'On Worker' },
      { value: V1RunningFilter.EVICTED, label: 'Evicted' },
    ],
  },
  {
    value: V1TaskStatus.QUEUED,
    label: 'Queued',
  },
  {
    value: V1TaskStatus.CANCELLED,
    label: 'Cancelled',
  },
];

export const useToolbarFilters = ({
  filterVisibility,
  filterActions,
  tableRows,
  createdAfter,
  workerId,
  parentTaskExternalId,
  triggeringEventExternalId,
  onlyTasks,
}: {
  filterVisibility: { [key: string]: boolean };
  filterActions: FilterActions;
  tableRows: V1TaskSummary[];
  createdAfter: string;
  workerId: string | undefined;
  parentTaskExternalId: string | undefined;
  triggeringEventExternalId: string | undefined;
  onlyTasks: boolean;
}): ToolbarFilters => {
  const [workflowSearch, setWorkflowSearch] = useState('');
  const [workflowCountsEnabled, setWorkflowCountsEnabled] = useState(false);
  const workflows = useWorkflows(workflowSearch);
  const canLoadWorkflowCounts = workflows.length <= WORKFLOW_COUNT_QUERY_LIMIT;
  const {
    counts,
    cappedWorkflowIds,
    isLoading: workflowCountsLoading,
  } = useWorkflowRunCounts(
    workflows,
    workflowCountsEnabled &&
      canLoadWorkflowCounts &&
      filterVisibility[workflowKey] !== false,
    createdAfter,
    filterActions.apiFilters.until,
    filterActions.apiFilters.statuses,
    filterActions.apiFilters.runningFilter,
    filterActions.apiFilters.additionalMetadata,
    filterActions.apiFilters.additionalMetadataOperator,
    filterActions.apiFilters.idempotencyKeys,
    workerId,
    parentTaskExternalId,
    triggeringEventExternalId,
    onlyTasks,
  );

  const workflowKeyFilters = useMemo(
    (): FilterOption[] =>
      buildWorkflowFilterOptions(workflows, counts, cappedWorkflowIds),
    [workflows, counts, cappedWorkflowIds],
  );

  const metadataFilters = useMemo(
    (): FilterOption[] =>
      buildMetadataFilterOptions(tableRows).map(({ key, value, count }) => ({
        label: `${key}=${value}`,
        value: `${key}:${value}`,
        count,
      })),
    [tableRows],
  );

  const timeRangeConfig: TimeRangeConfig = {
    onTimeWindowChange: (value: string) => {
      if (value !== 'custom') {
        filterActions.setTimeWindow(timeWindowSchema.parse(value));
      } else {
        filterActions.setCustomTimeRange({
          start:
            filterActions.apiFilters.since ||
            new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          end: filterActions.apiFilters.until || new Date().toISOString(),
        });
      }
    },
    onCreatedAfterChange: (date?: string) => {
      if (filterActions.isCustomTimeRange && filterActions.apiFilters.until) {
        filterActions.setCustomTimeRange({
          start:
            date || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          end: filterActions.apiFilters.until,
        });
      }
    },
    onFinishedBeforeChange: (date?: string) => {
      if (filterActions.isCustomTimeRange && filterActions.apiFilters.since) {
        filterActions.setCustomTimeRange({
          start: filterActions.apiFilters.since,
          end: date || new Date().toISOString(),
        });
      }
    },
    onClearTimeRange: () => filterActions.setCustomTimeRange(null),
    currentTimeWindow: filterActions.timeWindow,
    isCustomTimeRange: filterActions.isCustomTimeRange,
    createdAfter: filterActions.apiFilters.since,
    finishedBefore: filterActions.apiFilters.until,
  };

  return [
    {
      columnId: createdAtKey,
      title: 'Time Range',
      type: ToolbarType.TimeRange,
      timeRangeConfig,
      pinned: true,
    },
    {
      columnId: workflowKey,
      title: 'Workflow',
      options: workflowKeyFilters,
      type: ToolbarType.Checkbox,
      searchConfig: {
        onSearch: setWorkflowSearch,
      },
      onOpenChange: setWorkflowCountsEnabled,
      isLoading: workflowCountsLoading,
      notice: canLoadWorkflowCounts
        ? undefined
        : 'Search to narrow the list and load run counts.',
      pinned: true,
    },
    {
      columnId: statusKey,
      title: 'Status',
      options: workflowRunStatusFilters,
      type: ToolbarType.Checkbox,
    },
    {
      columnId: additionalMetadataKey,
      title: 'Metadata',
      type: ToolbarType.KeyValue,
      options: metadataFilters,
      operatorConfig: {
        value: filterActions.apiFilters.additionalMetadataOperator,
        onChange: (operator: 'AND' | 'OR') =>
          filterActions.setAdditionalMetadataOperator(
            operator === 'AND'
              ? V1AdditionalMetadataOperator.AND
              : V1AdditionalMetadataOperator.OR,
          ),
      },
      pinned: true,
    },
    {
      columnId: flattenDAGsKey,
      title: 'Flatten DAGs',
      type: ToolbarType.Switch,
      options: [
        { value: 'true', label: 'Flatten' },
        { value: 'false', label: 'All' },
      ],
    },
    {
      columnId: idempotencyKeyKey,
      title: 'Idempotency Key',
      type: ToolbarType.Array,
    },
  ].filter((filter) => filterVisibility[filter.columnId] != false);
};
